import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Centrifuge, type Subscription } from 'centrifuge';
import { useQueryClient } from '@tanstack/react-query';
import { subscribeChannel, unsubscribeChannel } from '@resida/realtime-client';
import { useAuth } from '../hooks/useAuth';
import { getCentrifugoToken } from '../lib/api';

const CENTRIFUGO_WS_URL =
  import.meta.env.VITE_CENTRIFUGO_WS_URL ||
  `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/centrifugo/connection/websocket`;

interface CentrifugoContextValue {
  client: Centrifuge | null;
  isConnected: boolean;
  isSubscribed: boolean;
  personalSubscription: Subscription | null;
}

// eslint-disable-next-line react-refresh/only-export-components
export const CentrifugoContext = createContext<CentrifugoContextValue>({
  client: null,
  isConnected: false,
  isSubscribed: false,
  personalSubscription: null,
});

export function CentrifugoProvider({ children }: { children: ReactNode }) {
  const { manager } = useAuth();
  const queryClient = useQueryClient();
  const subRef = useRef<Subscription | null>(null);
  const clientInstanceRef = useRef<Centrifuge | null>(null);
  const [client, setClient] = useState<Centrifuge | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [personalSubscription, setPersonalSubscription] = useState<Subscription | null>(null);

  useEffect(() => {
    if (!manager) return;

    let cancelled = false;
    // BR-18/JR-4 — after a WebSocket drop of any length, badges are correct on next read
    // (they're server-derived, never client-accumulated — BR-15) and stale list/detail data is
    // refreshed on re-subscribe. The FIRST subscribe of this provider instance is not a
    // recovery from a drop, so it must NOT trigger a full-cache invalidation (that would
    // refetch everything on every page load).
    let hasSubscribedOnce = false;

    const connect = async () => {
      try {
        const { token } = await getCentrifugoToken();
        if (cancelled) return;

        const instance = new Centrifuge(CENTRIFUGO_WS_URL, {
          token,
          debug: import.meta.env.DEV,
        });

        instance.on('connected', () => setIsConnected(true));
        instance.on('disconnected', () => setIsConnected(false));

        const channel = `personal:manager:${manager.id}`;
        const sub = subscribeChannel(instance, channel);
        sub.on('subscribed', () => {
          setIsSubscribed(true);
          if (hasSubscribedOnce) {
            // A RE-subscribe — recovering from a drop of unknown length. Counts need no
            // repair (server-derived); this refreshes stale list/detail query data instead.
            void queryClient.invalidateQueries();
          }
          hasSubscribedOnce = true;
        });
        sub.on('unsubscribed', () => setIsSubscribed(false));
        // RA-LIVE-RECONNECT — a dropped connection moves the subscription to 'subscribing' (not
        // 'unsubscribed'). Without clearing the flag here, the next 'subscribed' is never a
        // false -> true edge, so consumers (useCaseActivityRefresh) cannot see the reconnect.
        sub.on('subscribing', () => setIsSubscribed(false));
        sub.subscribe();
        subRef.current = sub;
        setPersonalSubscription(sub);

        instance.connect();
        clientInstanceRef.current = instance;
        setClient(instance);
      } catch (error) {
        console.warn('CentrifugoProvider: failed to connect', error);
      }
    };

    void connect();

    return () => {
      cancelled = true;
      if (clientInstanceRef.current) unsubscribeChannel(clientInstanceRef.current, subRef.current);
      subRef.current = null;
      clientInstanceRef.current?.disconnect();
      clientInstanceRef.current = null;
      setClient(null);
      setIsConnected(false);
      setIsSubscribed(false);
      setPersonalSubscription(null);
    };
  }, [manager?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CentrifugoContext.Provider value={{ client, isConnected, isSubscribed, personalSubscription }}>
      {isSubscribed && <span data-testid="centrifugo-connected" className="sr-only" aria-hidden="true" />}
      {children}
    </CentrifugoContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCentrifugoContext(): CentrifugoContextValue {
  return useContext(CentrifugoContext);
}
